import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { MessageSquare, ClipboardList, Rocket, Headphones } from "lucide-react";

const STEPS = [
    {
        n: "01",
        icon: MessageSquare,
        title: "Discuss Your Requirements",
        text: "We start by understanding your project, business needs, and technical requirements.",
        testid: "workflow-step-1",
    },
    {
        n: "02",
        icon: ClipboardList,
        title: "Plan the Solution",
        text: "We identify the appropriate service approach, scope, and implementation considerations.",
        testid: "workflow-step-2",
    },
    {
        n: "03",
        icon: Rocket,
        title: "Implement",
        text: "We deliver the agreed service, installation, configuration, or development work.",
        testid: "workflow-step-3",
    },
    {
        n: "04",
        icon: Headphones,
        title: "Support",
        text: "We provide relevant follow-up assistance and technical coordination within the agreed scope.",
        testid: "workflow-step-4",
    },
];

export default function WorkflowTimeline() {
    return (
        <section
            id="workflow"
            data-testid="workflow-section"
            className="grain relative overflow-hidden bg-navy py-20 lg:py-32"
        >
            <div
                className="pointer-events-none absolute left-1/2 top-0 h-72 w-[700px] -translate-x-1/2 opacity-30 blur-3xl"
                style={{
                    background:
                        "radial-gradient(circle, rgba(56,189,248,0.4), transparent 70%)",
                }}
                aria-hidden="true"
            />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
                <Reveal className="mb-16 max-w-2xl">
                    <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">
                        How We Work
                    </span>
                    <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                        A clear four-step service workflow.
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-slate-400">
                        From first conversation to ongoing support, we keep projects
                        understandable and manageable at every stage.
                    </p>
                </Reveal>

                <div className="relative grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    <div
                        className="absolute left-0 right-0 top-9 hidden h-px bg-gradient-to-r from-transparent via-sky-500/40 to-transparent lg:block"
                        aria-hidden="true"
                    />
                    {STEPS.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.1}>
                            <div
                                data-testid={s.testid}
                                className="relative h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur transition-colors hover:border-sky-400/40"
                            >
                                <div className="relative z-10 mb-5 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-brand text-white shadow-[0_10px_25px_rgba(2,132,199,0.4)]">
                                    <s.icon className="h-6 w-6" />
                                </div>
                                <span className="font-mono text-xs font-bold tracking-[0.2em] text-sky-400">
                                    STEP {s.n}
                                </span>
                                <h3 className="mt-2 font-display text-lg font-semibold text-white">
                                    {s.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                    {s.text}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
